import { useCallback, useEffect, useRef, useState } from 'react';
import { knowledgeAPI, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  GitBranch, Plus, RefreshCw, Link2, Trash2, FlaskConical, Loader2, Square, FolderTree, RotateCcw, Download,
} from 'lucide-react';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import KBRepoTreeDialog from '@/components/kb/KBRepoTreeDialog';
import { fechaYHora } from '../../lib/fechas';

function RepoTestResult({ result }) {
  if (!result) return null;
  return (
    <div
      className={`p-2 rounded-sm text-[10px] ${
        result.success
          ? 'bg-green-50 border border-green-200 text-green-700'
          : 'bg-red-50 border border-red-200 text-red-700'
      }`}
    >
      {result.success ? (
        <span className="font-medium">{result.message}</span>
      ) : (
        <div>
          <span className="font-medium">{result.error}</span>
          {result.hint && <p className="mt-0.5 text-red-500">{result.hint}</p>}
        </div>
      )}
    </div>
  );
}

function statusBadge(st) {
  if (st === 'ok') return <span className="text-green-600">ok</span>;
  if (st === 'noop') return <span className="text-zinc-500">sin cambios</span>;
  if (st === 'error') return <span className="text-red-600">error</span>;
  if (st === 'cancelled') return <span className="text-amber-600">cancelado</span>;
  return <span className="text-zinc-500">pending</span>;
}

function notifyKbIndexUpdated() {
  window.dispatchEvent(new CustomEvent('kb-index-updated'));
}

function formatSyncTime(iso) {
  if (!iso) return 'nunca';
  try {
    return fechaYHora(iso);
  } catch {
    return iso;
  }
}

export default function KBReposPanel() {
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    url: '',
    default_branch: 'main',
    auth_token: '',
    repo_id: '',
  });
  const [creating, setCreating] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [rowTest, setRowTest] = useState({ id: '', result: null, busy: false });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [purgeOnDelete, setPurgeOnDelete] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [treeRepoId, setTreeRepoId] = useState(null);
  const [autoSyncSaving, setAutoSyncSaving] = useState('');
  const prevOpRef = useRef({});

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await knowledgeAPI.listRepos();
      const list = res.data?.repos || [];
      setRepos(list);

      list.forEach((r) => {
        const prev = prevOpRef.current[r.repo_id] || {};
        if (prev.sync_state === 'syncing' && r.sync_state !== 'syncing') {
          const wasIndexing = (prev.sync_phase || '').includes('Indexando');
          if (wasIndexing) {
            const n = r.last_index_files_indexed ?? 0;
            const m = r.last_index_files_skipped ?? 0;
            if (r.last_index_status === 'ok') {
              toast.success(`Indexacion OK (${r.repo_id}): ${n} indexados, ${m} omitidos`);
              notifyKbIndexUpdated();
            } else if (r.last_index_status === 'noop') {
              toast.info(`Sin archivos nuevos que indexar (${r.repo_id})`);
            } else if (r.last_index_status === 'cancelled') {
              toast.info(`Indexacion cancelada (${r.repo_id})`);
            } else if (r.last_index_status === 'error') {
              toast.error(r.last_index_error || `Error al indexar (${r.repo_id})`);
            }
          } else {
            if (r.git_status === 'ok') {
              toast.success(`Descarga OK (${r.repo_id})`);
            } else if (r.git_status === 'cancelled') {
              toast.info(`Descarga cancelada (${r.repo_id})`);
            } else if (r.git_status === 'error') {
              toast.error(r.git_error || `Error al descargar (${r.repo_id})`);
            }
          }
        }
        prevOpRef.current[r.repo_id] = {
          sync_state: r.sync_state,
          sync_phase: r.sync_phase,
        };
      });
    } catch (e) {
      if (!silent) toast.error(formatApiError(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const anyRunning = repos.some((r) => r.sync_state === 'syncing');

  useEffect(() => {
    if (!anyRunning) return undefined;
    const id = setInterval(() => load(true), 2000);
    return () => clearInterval(id);
  }, [anyRunning, load]);

  const buildTestPayload = () => ({
    url: form.url.trim(),
    default_branch: form.default_branch.trim() || 'main',
    auth_token: form.auth_token,
    repo_id: form.repo_id.trim(),
  });

  const testConnection = async () => {
    if (!form.url.trim()) {
      toast.error('Indica la URL de clone');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await knowledgeAPI.testRepoConnection(buildTestPayload());
      setTestResult(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setTestResult({
        success: false,
        error: typeof detail === 'string' ? detail : formatApiError(e),
      });
    } finally {
      setTesting(false);
    }
  };

  const createRepo = async () => {
    if (!form.url.trim()) {
      toast.error('Indica la URL de clone');
      return;
    }
    setCreating(true);
    const toastId = toast.loading('Registrando repositorio...');
    try {
      await knowledgeAPI.createRepo({
        url: form.url.trim(),
        default_branch: form.default_branch.trim() || 'main',
        auth_token: form.auth_token,
        repo_id: form.repo_id.trim(),
      });
      toast.success('Repositorio registrado (sin descarga automatica)', { id: toastId });
      setForm({
        url: '',
        default_branch: 'main',
        auth_token: '',
        repo_id: '',
      });
      setTestResult(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e), { id: toastId });
    } finally {
      setCreating(false);
    }
  };

  const startDownload = async (repoId) => {
    try {
      const res = await knowledgeAPI.downloadRepo(repoId);
      const data = res.data || {};
      if (data.already_running) {
        toast.info('Ya hay una operacion en curso');
      } else {
        toast.success('Descarga iniciada');
      }
      load(true);
    } catch (e) {
      toast.error(formatApiError(e));
      load(true);
    }
  };

  const startIndex = async (repoId, { mode = 'incremental' } = {}) => {
    try {
      const res = await knowledgeAPI.indexRepo(repoId, { mode });
      const data = res.data || {};
      if (data.already_running) {
        toast.info('Ya hay una operacion en curso');
      } else {
        toast.success(mode === 'full' ? 'Indexacion completa iniciada' : 'Indexacion de cambios iniciada');
      }
      load(true);
    } catch (e) {
      toast.error(formatApiError(e));
      load(true);
    }
  };

  const cancelOperation = async (repoId) => {
    try {
      await knowledgeAPI.cancelRepoOperation(repoId);
      toast.info('Deteniendo operacion...');
      load(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const updateAutoSync = async (repoId, patch) => {
    setAutoSyncSaving(repoId);
    try {
      await knowledgeAPI.updateRepo(repoId, patch);
      await load(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAutoSyncSaving('');
    }
  };

  const testSavedRepo = async (repoId) => {
    setRowTest({ id: repoId, result: null, busy: true });
    try {
      const res = await knowledgeAPI.testSavedRepoConnection(repoId);
      setRowTest({ id: repoId, result: res.data, busy: false });
    } catch (e) {
      setRowTest({
        id: repoId,
        result: { success: false, error: formatApiError(e) },
        busy: false,
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await knowledgeAPI.deleteRepo(deleteTarget, { purge_index: purgeOnDelete });
      const purge = res.data?.purge;
      const msg = purgeOnDelete && purge
        ? `Repo eliminado. Purga: ${purge.qdrant_chunks_removed || 0} chunks Qdrant, ${purge.mongo_rows_removed || 0} filas indice.`
        : 'Repositorio eliminado';
      toast.success(msg);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const genWebhook = async (repoId) => {
    try {
      const res = await knowledgeAPI.generateRepoWebhookSecret(repoId);
      const secret = res.data?.webhook_secret;
      const url = `${window.location.origin}/api/knowledge/repos/${repoId}/webhook?tenant_id=TU_TENANT`;
      await navigator.clipboard.writeText(`URL: ${url}\nSecret: ${secret}`);
      toast.success('Webhook URL y secreto copiados al portapapeles');
    } catch {
      toast.error('Error generando webhook');
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <GitBranch className="w-4 h-4" /> Repositorios (pool tenant)
          </CardTitle>
          <p className="text-[10px] text-zinc-500 mt-1">
            URL de <strong>clone Git</strong> para indexar codigo en la KB (independiente de la habilidad GitLab de issues).
            Flujo: <strong>registrar</strong> → <strong>descargar</strong> → elegir exclusiones → <strong>indexar</strong>.
            El <strong>repo_id</strong> se usa en Config Capsula → Conocimiento y Agentes → Runtime.
            Repos privados requieren un <strong>PAT</strong> propio (scope <code>read_repository</code>); repos publicos no.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-zinc-500">URL de clone</Label>
              <Input
                placeholder="https://gitlab.ejemplo.com/grupo/proyecto.git"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">Branch</Label>
              <Input value={form.default_branch} onChange={(e) => setForm({ ...form, default_branch: e.target.value })} />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">Token / PAT (requerido si el repo es privado)</Label>
              <Input
                type="password"
                placeholder="PAT con scope read_repository"
                value={form.auth_token}
                onChange={(e) => setForm({ ...form, auth_token: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">ID del repositorio (repo_id)</Label>
              <Input
                placeholder="auto desde URL si vacio"
                value={form.repo_id}
                onChange={(e) => setForm({ ...form, repo_id: e.target.value })}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 items-start">
            <Button size="sm" onClick={createRepo} disabled={creating}>
              {creating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
              Agregar repo
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={testConnection}
              disabled={testing}
            >
              {testing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FlaskConical className="w-3 h-3 mr-1" />}
              Probar Conexion
            </Button>
          </div>
          <RepoTestResult result={testResult} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row justify-between py-3">
          <CardTitle className="text-sm">Repos registrados</CardTitle>
          <Button size="sm" variant="outline" onClick={() => load()} disabled={loading}>
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent>
          {repos.length === 0 ? (
            <p className="text-sm text-zinc-400">Sin repos. Agrega uno para registrarlo en el pool del tenant.</p>
          ) : (
            <div className="space-y-2">
              {repos.map((r) => {
                const running = r.sync_state === 'syncing';
                const prog = r.sync_progress || {};
                const pct = prog.total > 0 ? Math.round((prog.current / prog.total) * 100) : 0;
                return (
                  <div key={r.repo_id} className="border rounded-sm p-2 text-xs space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold flex items-center gap-1">
                          {running && <Loader2 className="w-3 h-3 animate-spin text-blue-500" />}
                          {r.repo_id}
                          {r.cloned && (
                            <span className="text-[9px] font-normal text-green-600 ml-1">clonado</span>
                          )}
                        </div>
                        <div className="text-zinc-500 truncate">{r.url} ({r.default_branch})</div>
                        <div className="text-zinc-400">
                          Git: {formatSyncTime(r.last_git_sync)} — {statusBadge(r.git_status || 'pending')}
                        </div>
                        <div className="text-zinc-400">
                          Index: {formatSyncTime(r.last_index_at)} — {statusBadge(r.last_index_status || 'pending')}
                        </div>
                        {running && (
                          <div className="mt-1 space-y-1">
                            <div className="text-blue-700 text-[10px]">
                              {r.sync_phase || 'En curso...'}
                              {prog.total > 0 && ` · ${prog.current}/${prog.total} archivos`}
                            </div>
                            {prog.current_file && (
                              <div className="text-zinc-500 text-[10px] truncate" title={prog.current_file}>
                                {prog.current_file}
                              </div>
                            )}
                            {prog.total > 0 && (
                              <div className="h-1 bg-zinc-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-blue-500 transition-all duration-300"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                        {r.git_status === 'error' && r.git_error && (
                          <div className="text-red-600 mt-1 break-words">{r.git_error}</div>
                        )}
                        {r.last_index_status === 'error' && r.last_index_error && (
                          <div className="text-red-600 mt-1 break-words">{r.last_index_error}</div>
                        )}
                        <div className="flex flex-wrap items-center gap-2 mt-2 text-[10px]">
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!r.auto_sync_enabled}
                              disabled={autoSyncSaving === r.repo_id}
                              onChange={(e) => updateAutoSync(r.repo_id, { auto_sync_enabled: e.target.checked })}
                            />
                            Auto-sync
                          </label>
                          {r.auto_sync_enabled && (
                            <div className="flex items-center gap-1">
                              <span className="text-zinc-500">cada</span>
                              <Input
                                type="number"
                                min={1}
                                className="h-6 w-14 text-[10px]"
                                defaultValue={r.auto_sync_interval_hours || 24}
                                disabled={autoSyncSaving === r.repo_id}
                                onBlur={(e) => {
                                  const h = Math.max(1, Number(e.target.value) || 24);
                                  if (h !== (r.auto_sync_interval_hours || 24)) {
                                    updateAutoSync(r.repo_id, { auto_sync_interval_hours: h });
                                  }
                                }}
                              />
                              <span className="text-zinc-500">h</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 flex-shrink-0 justify-end max-w-[240px]">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-[10px] h-7"
                          disabled={rowTest.busy && rowTest.id === r.repo_id}
                          onClick={() => testSavedRepo(r.repo_id)}
                          title="Probar conexion"
                        >
                          <FlaskConical className="w-3 h-3" />
                        </Button>
                        {running ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-[10px] h-7 text-amber-700 border-amber-300"
                            onClick={() => cancelOperation(r.repo_id)}
                          >
                            <Square className="w-3 h-3 mr-0.5" /> Detener
                          </Button>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-[10px] h-7"
                              onClick={() => startDownload(r.repo_id)}
                              title="Clone o pull del repositorio"
                            >
                              <Download className="w-3 h-3 mr-0.5" /> Descargar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-[10px] h-7"
                              onClick={() => startIndex(r.repo_id, { mode: 'incremental' })}
                              disabled={!r.cloned}
                              title="Indexar solo archivos cambiados desde el ultimo indexado"
                            >
                              Indexar cambios
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-[10px] h-7"
                              onClick={() => startIndex(r.repo_id, { mode: 'full' })}
                              disabled={!r.cloned}
                              title="Indexar todo el arbol (respeta exclusiones)"
                            >
                              <RotateCcw className="w-3 h-3 mr-0.5" /> Indexar todo
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-[10px] h-7"
                          onClick={() => setTreeRepoId(r.repo_id)}
                          title="Contenido a indexar"
                          disabled={!r.cloned}
                        >
                          <FolderTree className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" title="Generar webhook secret" onClick={() => genWebhook(r.repo_id)}>
                          <Link2 className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => { setDeleteTarget(r.repo_id); setPurgeOnDelete(true); }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    {rowTest.id === r.repo_id && <RepoTestResult result={rowTest.result} />}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <KBRepoTreeDialog
        repoId={treeRepoId}
        open={!!treeRepoId}
        onOpenChange={(open) => { if (!open) setTreeRepoId(null); }}
        onSaved={() => load(true)}
      />

      <ConfirmActionDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={`Eliminar repositorio ${deleteTarget || ''}`}
        description="El repo dejara de estar disponible. Opcionalmente purga chunks indexados en Qdrant y filas en kb_file_index."
        confirmLabel={deleting ? 'Eliminando...' : 'Eliminar'}
        onConfirm={confirmDelete}
        destructive
        confirmDisabled={deleting}
      >
        <label className="flex items-center gap-2 text-xs px-6 cursor-pointer">
          <input
            type="checkbox"
            checked={purgeOnDelete}
            onChange={(e) => setPurgeOnDelete(e.target.checked)}
          />
          Purgar chunks indexados de este repo (Qdrant + indice)
        </label>
      </ConfirmActionDialog>
    </div>
  );
}
